/**
 * terms.js — UK ↔ US real-estate localisation switch for Foundation.
 *
 * Foundation is architected to run the same site template in either market.
 * A tenant's brand carries `market: "uk" | "us"` (absent ⇒ "uk"), and every
 * shared component renders the right label/currency without a per-page
 * conditional by calling `t(key, market)`, `lbl(key, market)` or
 * `fmtPrice(amount, market, …)`.
 *
 * Design rule: **UK is the default and is never changed by this file.** Every
 * helper falls back to the UK flavour, so existing UK tenants render exactly
 * as before — only a tenant that opts into `market: "us"` sees US wording.
 */

/** Short, one-or-two-word labels. */
export const TERMS = {
  // People / roles
  vendor:        { uk: "Vendor",        us: "Seller" },
  vendors:       { uk: "Vendors",       us: "Sellers" },
  landlord:      { uk: "Landlord",      us: "Landlord" },
  landlords:     { uk: "Landlords",     us: "Landlords" },
  tenant:        { uk: "Tenant",        us: "Renter" },
  tenants:       { uk: "Tenants",       us: "Renters" },
  buyer:         { uk: "Buyer",         us: "Buyer" },

  // Tenure / contracts
  tenancy:       { uk: "Tenancy",       us: "Lease" },
  tenancies:     { uk: "Tenancies",     us: "Leases" },
  letting:       { uk: "Letting",       us: "Rental" },
  lettings:      { uk: "Lettings",      us: "Rentals" },
  let_verb:      { uk: "Let",           us: "Rent" },
  letting_verb:  { uk: "Letting",       us: "Renting" },
  to_let:        { uk: "To let",        us: "For rent" },
  freehold:      { uk: "Freehold",      us: "Fee simple" },
  leasehold:     { uk: "Leasehold",     us: "Condo/HOA" },

  // Status
  under_offer:   { uk: "Under offer",   us: "Under contract" },
  sold_stc:      { uk: "Sold STC",      us: "Under contract" },
  let_agreed:    { uk: "Let Agreed",    us: "Lease pending" },
  sold:          { uk: "Sold",          us: "Sold" },
  let_done:      { uk: "Let",           us: "Leased" },
  completion:    { uk: "completion",    us: "closing" },

  // Money / property
  postcode:      { uk: "Postcode",      us: "ZIP code" },
  postcode_lc:   { uk: "postcode",      us: "ZIP code" },
  pcm:           { uk: "pcm",           us: "/mo" },
  epc:           { uk: "EPC",           us: "energy rating" },
  stamp_duty:    { uk: "Stamp Duty",    us: "Transfer tax" },

  // Trade / brand
  estate_agent:  { uk: "estate agent",  us: "real estate agent" },
  estate_agents: { uk: "estate agents", us: "real estate agents" },
  agency:        { uk: "agency",        us: "brokerage" },
  agency_cap:    { uk: "Agency",        us: "Brokerage" },
  valuation:     { uk: "valuation",     us: "home valuation" },
  valuation_cap: { uk: "Valuation",     us: "Home valuation" },

  // ---- CRM vocabulary -------------------------------------------------
  // Everything below is CRM-side wording. The public site had its terms
  // above; these are the words an agent sees at their desk all day, and
  // getting them wrong is what makes a US agent feel the tool was built
  // for somebody else.

  // Appointments
  viewing:       { uk: "viewing",       us: "showing" },
  viewings:      { uk: "viewings",      us: "showings" },
  viewing_cap:   { uk: "Viewing",       us: "Showing" },
  viewings_cap:  { uk: "Viewings",      us: "Showings" },

  // Winning the listing
  appraisal:     { uk: "appraisal",     us: "CMA" },
  appraisals:    { uk: "appraisals",    us: "CMAs" },
  appraisal_cap: { uk: "Appraisal",     us: "CMA" },
  appraisals_cap:{ uk: "Appraisals",    us: "CMAs" },
  // The UK "market appraisal" and the US "comparative market analysis" are
  // the same visit with a different name; the long form is spelled out the
  // first time it appears on a page.
  appraisal_long:{ uk: "Market appraisal", us: "Comparative market analysis" },
  instruction:   { uk: "instruction",   us: "listing agreement" },
  instructions:  { uk: "instructions",  us: "listing agreements" },
  instructed:    { uk: "Instructed",    us: "Listed" },
  instruction_cap:{ uk: "Instruction",  us: "Listing agreement" },

  // The transaction
  chain:         { uk: "chain",         us: "escrow" },
  chain_cap:     { uk: "Chain",         us: "Escrow" },
  solicitor:     { uk: "solicitor",     us: "title company" },
  solicitors:    { uk: "solicitors",    us: "title companies" },
  solicitor_cap: { uk: "Solicitor",     us: "Title company" },
  conveyancing:  { uk: "conveyancing",  us: "closing" },
  completion_cap:{ uk: "Completion",    us: "Closing" },
  exchange:      { uk: "Exchange",      us: "Contract executed" },
  memo_of_sale:  { uk: "Memo of Sale",  us: "Sale confirmation" },
  sales_progression: { uk: "Sales progression", us: "Transaction management" },

  // Property attributes
  tenure:        { uk: "Tenure",        us: "Ownership" },
  council_tax:   { uk: "Council Tax",   us: "Property tax" },
  branch:        { uk: "branch",        us: "office" },
  branches:      { uk: "branches",      us: "offices" },
  branch_cap:    { uk: "Branch",        us: "Office" },
  branches_cap:  { uk: "Branches",      us: "Offices" },

  // Portals. Rightmove and Zoopla have no US equivalent; the MLS is the
  // single place a US listing goes, so the whole feed concept is renamed
  // rather than mapped portal-for-portal.
  portal:        { uk: "portal",        us: "MLS" },
  portals:       { uk: "portals",       us: "MLS" },
  portal_cap:    { uk: "Portal",        us: "MLS" },
  portal_feed:   { uk: "Portal feed",   us: "MLS feed" },

  // Compliance
  aml:           { uk: "Anti-Money-Laundering", us: "Identity verification" },
  aml_short:     { uk: "AML",           us: "ID check" },

  // People, CRM-side
  applicant:     { uk: "applicant",     us: "buyer lead" },
  applicants:    { uk: "applicants",    us: "buyer leads" },
  applicant_cap: { uk: "Applicant",     us: "Buyer lead" },
  applicants_cap:{ uk: "Applicants",    us: "Buyer leads" },
  enquiry:       { uk: "enquiry",       us: "inquiry" },
  enquiries:     { uk: "enquiries",     us: "leads" },
  enquiry_cap:   { uk: "Enquiry",       us: "Inquiry" },
  enquiries_cap: { uk: "Enquiries",     us: "Leads" },

  // Commission. The UK charges the seller a percentage fee; the US splits a
  // commission between two sides and calls the agent's share GCI.
  fee:           { uk: "fee",           us: "commission" },
  fee_cap:       { uk: "Fee",           us: "Commission" },
  fees_cap:      { uk: "Fees",          us: "Commission" },
};

/**
 * Longer, sentence-level phrases that differ between markets. Kept separate
 * from TERMS so the short-label table stays scannable.
 */
export const LABELS = {
  // Hero / CTAs
  free_valuation:      { uk: "Free 60-second valuation",     us: "Get your home's value" },
  free_valuation_alt:  { uk: "Free property valuation",      us: "Request a free home valuation" },
  free_valuation_cta:  { uk: "Get my free valuation",        us: "Request my free valuation" },
  register_buyer:      { uk: "Register as a buyer",          us: "Register as a buyer" },
  independent_agent:   { uk: "Independent estate agent",     us: "Independent brokerage" },

  // Homepage services
  svc_sales_title:     { uk: "Residential sales",            us: "Residential sales" },
  svc_sales_body:      { uk: "Bespoke marketing, professional photography, honest advice, from valuation to completion.",
                         us: "Full-service marketing, professional photography, and honest advice, from listing to closing." },
  svc_lettings_title:  { uk: "Lettings & management",        us: "Rentals & property management" },
  svc_lettings_body:   { uk: "Full or tenant-find services with rigorous referencing and 24/7 landlord support.",
                         us: "Full management or tenant placement with rigorous screening and 24/7 owner support." },
  svc_newhomes_title:  { uk: "New homes & land",             us: "New construction & land" },
  svc_newhomes_body:   { uk: "Specialist off-plan and new build advice for developers, investors and first-time buyers.",
                         us: "Specialist new-build and land guidance for builders, investors and first-time buyers." },
  svc_tools_title:     { uk: "Free financial tools",         us: "Free buyer tools" },
  svc_tools_body:      { uk: "Stamp duty, mortgage and rental yield calculators. Free for you to use whenever you need.",
                         us: "Mortgage, affordability and rental-yield calculators. Free to use whenever you need." },

  // Stats strip
  stat_on_market:      { uk: "Live on the market now",       us: "Active listings now" },
  stat_sold_or_let:    { uk: "Sold or let through us",       us: "Sold or leased through us" },

  // Data provenance
  data_backed:         { uk: "HM Land Registry data",        us: "recent local sales data" },
  data_backed_long:    { uk: "using HM Land Registry data",  us: "using recent local sales and public records" },

  // Sell page
  sell_valuation_cta:  { uk: "Free property valuation",      us: "Request a free home valuation" },
  no_sale_no_fee:      { uk: "No sale, no fee.",             us: "No upfront costs. You pay at closing." },

  // Rent / Let page
  rental_valuation_cta:{ uk: "Get a free rental valuation",  us: "Get a free rent analysis" },
  landlords_choose:    { uk: "Why landlords choose us",      us: "Why owners choose us" },

  // Footer
  footer_default_intro:{ uk: "Independent estate agents covering",
                         us: "An independent brokerage serving" },
};

/** Resolve the effective market, defaulting to UK. */
export function market(m) {
  return m === "us" ? "us" : "uk";
}

/** Short label lookup. `t("vendor", "us") → "Seller"`. */
export function t(key, m = "uk") {
  const row = TERMS[key];
  if (!row) return key;
  return row[market(m)] || row.uk;
}

/** Sentence-level label lookup. `lbl("free_valuation", "us")`. */
export function lbl(key, m = "uk") {
  const row = LABELS[key];
  if (!row) return key;
  return row[market(m)] || row.uk;
}

/** Currency symbol per market. */
export function currencySymbol(m = "uk") {
  return market(m) === "us" ? "$" : "£";
}

/** Locale used for thousands separators. */
export function moneyLocale(m = "uk") {
  return market(m) === "us" ? "en-US" : "en-GB";
}

/**
 * Format a money amount for the active market.
 *   fmtPrice(925000, "us")               → "$925,000"
 *   fmtPrice(2500, "us", { rent: true }) → "$2,500/mo"
 *   fmtPrice(2500, "uk", { rent: true }) → "£2,500 pcm"
 */
export function fmtPrice(amount, m = "uk", { rent = false } = {}) {
  const n = Math.round(Number(amount) || 0);
  // The sign goes outside the symbol. A shortfall against target is
  // "-$331,351", never "$-331,351", which reads as a typo and undermines
  // every other figure on the page.
  const sign = n < 0 ? "-" : "";
  const base = `${sign}${currencySymbol(m)}${Math.abs(n).toLocaleString(moneyLocale(m))}`;
  if (!rent) return base;
  return market(m) === "us" ? `${base}/mo` : `${base} pcm`;
}
